import React, { useState, useEffect } from 'react';
import { api } from '../../shared/utils/api';
import { SUBJECT_COLOURS } from '../../shared/constants/subjects';
import SpreadsheetImport from '../mcq/SpreadsheetImport';
import AccountSettings from './AccountSettings';
import PlannerSettings from './PlannerSettings';
import AppearanceSettings from './AppearanceSettings';
import DataSettings from './DataSettings';

const DEFAULT_STATUSES = ['Not started', 'Learning', 'Practised', 'Confident', 'Needs review'];
const DEFAULT_STATUS_COLORS = ['#F3F4F6', '#DBEAFE', '#FEF3C7', '#D1FAE5', '#FEE2E2'];
function getDefaultColor(idx) { return DEFAULT_STATUS_COLORS[idx] || '#F3F4F6'; }

// Defaults sourced from subjects.js — single source of truth for brand colours.
// Mapping: module→BLP (blue), mcq→Criminal (pink), test→Wills (teal),
//          recall→Dispute (violet), needs_work→Tort (red), moderate→Contract (amber)
const PLANNER_COLORS = [
  { key: 'color_module',             label: 'Module sessions',     default: SUBJECT_COLOURS['Business Law and Practice'] },
  { key: 'color_mcq',                label: 'MCQ sessions',        default: SUBJECT_COLOURS['Criminal Law and Practice'] },
  { key: 'color_test',               label: 'Test sessions',       default: SUBJECT_COLOURS['Wills and the Administration of Estates'] },
  { key: 'color_recall',             label: 'Recall sessions',     default: SUBJECT_COLOURS['Dispute Resolution'] },
  { key: 'color_review_needs_work',  label: 'Review: needs work', default: SUBJECT_COLOURS['Tort'] },
  { key: 'color_review_moderate',    label: 'Review: moderate',   default: SUBJECT_COLOURS['Contract Law'] },
];

const DASH_TOGGLES = [
  { key: 'dash_show_countdown',   label: 'Exam countdowns',         desc: 'Shows days remaining to your FLK1 and FLK2 exams' },
  { key: 'dash_show_calibration', label: 'Calibration alerts',      desc: 'Flags topics where your confidence and MCQ accuracy differ by 20 pp or more' },
  { key: 'dash_show_checkin',     label: 'Recall nudge',            desc: 'Suggests topics you have not reviewed recently' },
  { key: 'dash_show_priority',    label: 'Priority topics',         desc: 'Your weakest FLK1 and FLK2 topics, ranked by priority' },
  { key: 'dash_show_touched',     label: 'How are you feeling?',    desc: 'Quick confidence sliders for your highest-priority topics' },
  { key: 'dash_show_mcq',         label: 'MCQ milestone',           desc: 'Progress bar showing total MCQs completed' },
  { key: 'dash_show_legend',      label: 'Priority tier legend',    desc: 'Shows what each priority level means' },
  { key: 'dash_show_principle',   label: 'Revision principles',     desc: 'Guidance on effective revision techniques' },
];

const TABS = [
  { key: 'account',  label: 'Account' },
  { key: 'study',    label: 'Study' },
  { key: 'display',  label: 'Display' },
  { key: 'data',     label: 'Data' },
  { key: 'billing',  label: 'Billing' },
];

export default function SettingsPage({ onRestartTour, initialTab, theme = 'default', onThemeChange }) {
  const [activeTab, setActiveTab] = useState(initialTab || 'account');
  const [settings, setSettings] = useState({});
  const [user, setUser] = useState({});
  const [name, setName]       = useState('');
  const [flk1Date, setFlk1Date] = useState('');
  const [flk2Date, setFlk2Date] = useState('');
  const [provider, setProvider] = useState('');
  const [mcqTarget, setMcqTarget] = useState('40');
  const [showEmailEdit, setShowEmailEdit] = useState(false);
  const [emailNew, setEmailNew] = useState('');
  const [emailPw, setEmailPw] = useState('');
  const [emailError, setEmailError] = useState('');
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailSaved, setEmailSaved] = useState(false);
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw]         = useState('');
  const [pwError, setPwError]     = useState('');
  const [pwSaved, setPwSaved]     = useState(false);
  const [saved, setSaved] = useState({});
  const [showImport, setShowImport] = useState(false);
  const [targetConf, setTargetConf] = useState('0.70');
  const [customTargetVal, setCustomTargetVal] = useState('');
  // Planner v2 settings
  const [plannerStudyDays, setPlannerStudyDays] = useState(['mon','tue','wed','thu','fri','sat','sun']);
  const [plannerMaxSessions, setPlannerMaxSessions] = useState(8);
  const [plannerExamBuffer, setPlannerExamBuffer] = useState(14);
  const [plannerRevisionThreshold, setPlannerRevisionThreshold] = useState(0.20);
  const [plannerSettingsSaved, setPlannerSettingsSaved] = useState(false);
  const [plannerSettingsSaving, setPlannerSettingsSaving] = useState(false);
  const [plannerRecommendation, setPlannerRecommendation] = useState(null);
  // New settings (2026-04-10)
  const [confThreshRed, setConfThreshRed] = useState('0.40');
  const [confThreshGreen, setConfThreshGreen] = useState('0.70');
  const [dashMetricLayout, setDashMetricLayout] = useState('single');
  const [plannerSessionColours, setPlannerSessionColours] = useState('simplified');
  const [focusTopicCount, setFocusTopicCount] = useState('10');
  const [showMcqMilestone, setShowMcqMilestone] = useState('1');
  // Account deletion
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deleting, setDeleting] = useState(false);
  // Status list (full ordered list stored in custom_statuses; empty = use defaults)
  const [allStatuses, setAllStatuses] = useState(DEFAULT_STATUSES.map((label, idx) => ({ label, color: getDefaultColor(idx) })));
  const [newStatus, setNewStatus] = useState('');
  // Spaced rep intervals
  const [srRedDays, setSrRedDays] = useState('7');
  const [srAmberDays, setSrAmberDays] = useState('14');
  // Billing
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState('');
  // Refund
  const [showRefundForm, setShowRefundForm] = useState(false);
  const [refundReason, setRefundReason] = useState('');
  const [refundWentWrong, setRefundWentWrong] = useState('');
  const [refundImprovement, setRefundImprovement] = useState('');
  const [refundSubmitting, setRefundSubmitting] = useState(false);
  const [refundError, setRefundError] = useState('');
  const [refundDone, setRefundDone] = useState(false);

  useEffect(() => {
    Promise.all([api('/api/settings'), api('/api/me')]).then(([s, u]) => {
      setSettings(s);
      setUser(u);
      setName(u.name || '');
      setFlk1Date(s.flk1_exam_date || (u.exam_date_flk1 ? u.exam_date_flk1.slice(0, 10) : '') || '');
      setFlk2Date(s.flk2_exam_date || (u.exam_date_flk2 ? u.exam_date_flk2.slice(0, 10) : '') || '');
      setProvider(s.material_provider || '');
      setMcqTarget(s.mcq_daily_target || '40');
      try {
        const cs = JSON.parse(s.custom_statuses || '[]');
        if (cs.length > 0) {
          // Support both string array and object array
          const normalized = cs.map((item, idx) =>
            typeof item === 'string' ? { label: item, color: getDefaultColor(idx) } : item
          );
          setAllStatuses(normalized);
        } else {
          setAllStatuses(DEFAULT_STATUSES.map((label, idx) => ({ label, color: getDefaultColor(idx) })));
        }
      } catch {
        setAllStatuses(DEFAULT_STATUSES.map((label, idx) => ({ label, color: getDefaultColor(idx) })));
      }
      setSrRedDays(s.spaced_rep_red_days || '7');
      setSrAmberDays(s.spaced_rep_amber_days || '14');
      setTargetConf(s.target_confidence || '0.70');
      setConfThreshRed(s.confidence_threshold_red || '0.40');
      setConfThreshGreen(s.confidence_threshold_green || '0.70');
      setDashMetricLayout(s.dashboard_metric_layout || 'single');
      setPlannerSessionColours(s.planner_session_colours || 'simplified');
      setFocusTopicCount(s.focus_topic_count || '10');
      setShowMcqMilestone(s.show_mcq_milestone ?? '1');
      // If stored target_confidence is not one of the presets, it's custom
      const presets = ['0.60', '0.70', '0.80'];
      const storedTarget = s.target_confidence || '0.70';
      if (!presets.includes(storedTarget)) {
        setCustomTargetVal(String(Math.round(parseFloat(storedTarget) * 100)));
      }
    });
  }, []);

  useEffect(() => {
    api('/api/planner/settings').then(ps => {
      if (!ps || ps.error) return;
      if (ps.study_days) {
        setPlannerStudyDays(ps.study_days.split(',').map(d => d.trim()).filter(Boolean));
      }
      if (ps.max_sessions_per_day != null) setPlannerMaxSessions(ps.max_sessions_per_day);
      if (ps.exam_buffer_days != null) setPlannerExamBuffer(ps.exam_buffer_days);
      if (ps.revision_start_threshold != null) setPlannerRevisionThreshold(ps.revision_start_threshold);
      if (ps._recommendation) setPlannerRecommendation(ps._recommendation);
    }).catch(() => {});
  }, []);

  const savePlannerSettings = async (overrides = {}) => {
    setPlannerSettingsSaving(true);
    const payload = {
      study_days: (overrides.study_days ?? plannerStudyDays).join(','),
      max_sessions_per_day: overrides.max_sessions_per_day ?? plannerMaxSessions,
      exam_buffer_days: overrides.exam_buffer_days ?? plannerExamBuffer,
      revision_start_threshold: overrides.revision_start_threshold ?? plannerRevisionThreshold,
    };
    await api('/api/planner/settings', { method: 'PUT', body: JSON.stringify(payload) });
    // Re-fetch to get the updated recommendation
    try {
      const ps = await api('/api/planner/settings');
      if (ps?._recommendation) setPlannerRecommendation(ps._recommendation);
    } catch {}
    setPlannerSettingsSaving(false);
    setPlannerSettingsSaved(true);
    setTimeout(() => setPlannerSettingsSaved(false), 2000);
  };

  const flash = (key) => {
    setSaved(prev => ({ ...prev, [key]: true }));
    setTimeout(() => setSaved(prev => ({ ...prev, [key]: false })), 2000);
  };

  const saveSetting = async (key, value) => {
    await api('/api/settings', { method: 'POST', body: JSON.stringify({ [key]: value }) });
    flash(key);
  };

  const saveName = async () => {
    if (!name.trim()) return;
    await api('/api/account', { method: 'PATCH', body: JSON.stringify({ name: name.trim() }) });
    flash('name');
  };

  const saveDates = async () => {
    const settingsUp = {};
    const accountUp = {};
    if (flk1Date) { settingsUp.flk1_exam_date = flk1Date; accountUp.exam_date_flk1 = flk1Date; }
    if (flk2Date) { settingsUp.flk2_exam_date = flk2Date; accountUp.exam_date_flk2 = flk2Date; }
    await Promise.all([
      api('/api/settings', { method: 'POST', body: JSON.stringify(settingsUp) }),
      api('/api/account',  { method: 'PATCH', body: JSON.stringify(accountUp) }),
    ]);
    flash('dates');
  };

  const saveMcqTarget = () => {
    const v = Math.max(1, Math.min(999, parseInt(mcqTarget) || 40)).toString();
    setMcqTarget(v);
    saveSetting('mcq_daily_target', v);
  };

  const saveAllStatuses = (list) => {
    setAllStatuses(list);
    saveSetting('custom_statuses', JSON.stringify(list));
  };
  const moveStatusUp = (idx) => {
    if (idx === 0) return;
    const u = [...allStatuses];
    [u[idx - 1], u[idx]] = [u[idx], u[idx - 1]];
    saveAllStatuses(u);
  };
  const moveStatusDown = (idx) => {
    if (idx === allStatuses.length - 1) return;
    const u = [...allStatuses];
    [u[idx], u[idx + 1]] = [u[idx + 1], u[idx]];
    saveAllStatuses(u);
  };
  const removeStatus = (idx) => {
    saveAllStatuses(allStatuses.filter((_, i) => i !== idx));
  };
  const addStatus = () => {
    const s = newStatus.trim();
    if (!s || allStatuses.some(x => x.label === s)) return;
    saveAllStatuses([...allStatuses, { label: s, color: getDefaultColor(allStatuses.length) }]);
    setNewStatus('');
  };

  const saveSpacedRep = () => {
    const red = Math.max(1, Math.min(90, parseInt(srRedDays) || 7)).toString();
    const amber = Math.max(1, Math.min(90, parseInt(srAmberDays) || 14)).toString();
    setSrRedDays(red); setSrAmberDays(amber);
    saveSetting('spaced_rep_red_days', red);
    saveSetting('spaced_rep_amber_days', amber);
  };

  const updateEmail = async (e) => {
    e.preventDefault();
    if (!emailNew.trim() || !emailPw.trim()) return;
    setEmailSaving(true); setEmailError('');
    const res = await api('/api/update-email', {
      method: 'POST',
      body: JSON.stringify({ new_email: emailNew.trim(), password: emailPw.trim() }),
    });
    setEmailSaving(false);
    if (res.error) { setEmailError(res.error); }
    else {
      setUser(prev => ({ ...prev, email: emailNew.trim() }));
      setEmailSaved(true); setShowEmailEdit(false); setEmailNew(''); setEmailPw('');
      setTimeout(() => setEmailSaved(false), 3000);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setPwError('');
    if (newPw.length < 8) { setPwError('New password must be at least 8 characters'); return; }
    const res = await api('/api/change-password', {
      method: 'POST',
      body: JSON.stringify({ current_password: currentPw, new_password: newPw }),
    });
    if (res.ok) {
      setPwSaved(true); setCurrentPw(''); setNewPw('');
      setTimeout(() => setPwSaved(false), 3000);
    } else {
      setPwError(res.error || 'Password change failed. Check your current password and try again.');
    }
  };

  const deleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE' || !deletePassword.trim()) return;
    setDeleting(true);
    setDeleteError('');
    const res = await api('/api/account', {
      method: 'DELETE',
      body: JSON.stringify({ confirm: deleteConfirmText, password: deletePassword }),
    });
    setDeleting(false);
    if (res.deleted) {
      localStorage.removeItem('sqetrack_token');
      window.location.href = 'https://sqetrack.co.uk/?deleted=1';
    } else {
      setDeleteError(res.error || 'Account deletion failed. Check your password and try again.');
    }
  };

  const openPortal = async () => {
    setPortalLoading(true);
    setPortalError('');
    try {
      const res = await api('/api/create-portal-session', { method: 'POST' });
      if (res.error) { setPortalError(res.error); }
      else if (res.url) { window.location.href = res.url; }
    } catch { setPortalError('Could not connect to the server. Try again in a moment.'); }
    finally { setPortalLoading(false); }
  };

  const submitRefund = async (e) => {
    e.preventDefault();
    // Min length validation
    if (refundReason.length > 0 && refundReason.length < 20) {
      setRefundError('The reason field needs at least 20 characters.');
      return;
    }
    if (refundImprovement.length > 0 && refundImprovement.length < 20) {
      setRefundError('The improvement field needs at least 20 characters.');
      return;
    }
    setRefundSubmitting(true);
    setRefundError('');
    const res = await api('/api/refund-request', {
      method: 'POST',
      body: JSON.stringify({ reason: refundReason, what_went_wrong: refundWentWrong, improvement: refundImprovement }),
    });
    setRefundSubmitting(false);
    if (res.error) { setRefundError(res.error); }
    else { setRefundDone(true); setShowRefundForm(false); }
  };

  if (showImport) {
    return <SpreadsheetImport onBack={() => setShowImport(false)} onDone={() => setShowImport(false)} />;
  }

  // Calculate refund eligibility
  const isLifetime = user.tier === 'tracker_practice' || user.tier === 'complete';
  const isMonthly = user.tier === 'monthly';
  const withinRefundWindow = isLifetime && user.paid_at
    ? (Date.now() - new Date(user.paid_at).getTime()) < 14 * 86400000
    : false;

  return (
    <div className="page-container">
    <div className="settings-page">
      <div className="settings-header">
        <h1 className="page-heading">Settings</h1>
      </div>

      {/* Tab navigation */}
      <div className="settings-tabs">
        {TABS.map(t => (
          <button
            key={t.key}
            className={`settings-tab-btn ${activeTab === t.key ? 'active' : ''}`}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Account tab */}
      {activeTab === 'account' && (
        <AccountSettings
          name={name} setName={setName} saveName={saveName} saved={saved}
          user={user}
          showEmailEdit={showEmailEdit} setShowEmailEdit={setShowEmailEdit}
          emailNew={emailNew} setEmailNew={setEmailNew}
          emailPw={emailPw} setEmailPw={setEmailPw}
          emailError={emailError} emailSaving={emailSaving} emailSaved={emailSaved}
          updateEmail={updateEmail}
          currentPw={currentPw} setCurrentPw={setCurrentPw}
          newPw={newPw} setNewPw={setNewPw}
          pwError={pwError} pwSaved={pwSaved} changePassword={changePassword}
          flk1Date={flk1Date} setFlk1Date={setFlk1Date}
          flk2Date={flk2Date} setFlk2Date={setFlk2Date}
          saveDates={saveDates}
          showDeleteConfirm={showDeleteConfirm} setShowDeleteConfirm={setShowDeleteConfirm}
          deleteConfirmText={deleteConfirmText} setDeleteConfirmText={setDeleteConfirmText}
          deletePassword={deletePassword} setDeletePassword={setDeletePassword}
          deleteError={deleteError} deleting={deleting} deleteAccount={deleteAccount}
        />
      )}

      {/* Study tab */}
      {activeTab === 'study' && (
        <PlannerSettings
          flk1Date={flk1Date} setFlk1Date={setFlk1Date}
          flk2Date={flk2Date} setFlk2Date={setFlk2Date}
          saved={saved} saveDates={saveDates}
          provider={provider} setProvider={setProvider} saveSetting={saveSetting}
          mcqTarget={mcqTarget} setMcqTarget={setMcqTarget} saveMcqTarget={saveMcqTarget}
          targetConf={targetConf} setTargetConf={setTargetConf}
          customTargetVal={customTargetVal} setCustomTargetVal={setCustomTargetVal}
          allStatuses={allStatuses} newStatus={newStatus} setNewStatus={setNewStatus}
          addStatus={addStatus} moveStatusUp={moveStatusUp} moveStatusDown={moveStatusDown}
          removeStatus={removeStatus} saveAllStatuses={saveAllStatuses}
          srRedDays={srRedDays} setSrRedDays={setSrRedDays}
          srAmberDays={srAmberDays} setSrAmberDays={setSrAmberDays}
          saveSpacedRep={saveSpacedRep}
          settings={settings} setSettings={setSettings}
          plannerStudyDays={plannerStudyDays} setPlannerStudyDays={setPlannerStudyDays}
          plannerMaxSessions={plannerMaxSessions} setPlannerMaxSessions={setPlannerMaxSessions}
          plannerExamBuffer={plannerExamBuffer} setPlannerExamBuffer={setPlannerExamBuffer}
          plannerRevisionThreshold={plannerRevisionThreshold} setPlannerRevisionThreshold={setPlannerRevisionThreshold}
          plannerRecommendation={plannerRecommendation} setPlannerRecommendation={setPlannerRecommendation}
          plannerSettingsSaved={plannerSettingsSaved} plannerSettingsSaving={plannerSettingsSaving}
          savePlannerSettings={savePlannerSettings}
          focusTopicCount={focusTopicCount} setFocusTopicCount={setFocusTopicCount}
          flash={flash}
          getDefaultColor={getDefaultColor}
        />
      )}

      {/* Display tab */}
      {activeTab === 'display' && (
        <AppearanceSettings
          theme={theme} onThemeChange={onThemeChange}
          settings={settings} setSettings={setSettings} saveSetting={saveSetting} saved={saved}
          PLANNER_COLORS={PLANNER_COLORS} DASH_TOGGLES={DASH_TOGGLES}
          confThreshRed={confThreshRed} setConfThreshRed={setConfThreshRed}
          confThreshGreen={confThreshGreen} setConfThreshGreen={setConfThreshGreen}
          dashMetricLayout={dashMetricLayout} setDashMetricLayout={setDashMetricLayout}
          plannerSessionColours={plannerSessionColours} setPlannerSessionColours={setPlannerSessionColours}
          showMcqMilestone={showMcqMilestone} setShowMcqMilestone={setShowMcqMilestone}
        />
      )}

      {/* Data and Billing tabs */}
      {(activeTab === 'data' || activeTab === 'billing') && (
        <DataSettings
          activeTab={activeTab}
          setShowImport={setShowImport}
          user={user} openPortal={openPortal} portalLoading={portalLoading} portalError={portalError}
          isLifetime={isLifetime} isMonthly={isMonthly} withinRefundWindow={withinRefundWindow}
          refundDone={refundDone} showRefundForm={showRefundForm} setShowRefundForm={setShowRefundForm}
          refundReason={refundReason} setRefundReason={setRefundReason}
          refundWentWrong={refundWentWrong} setRefundWentWrong={setRefundWentWrong}
          refundImprovement={refundImprovement} setRefundImprovement={setRefundImprovement}
          refundSubmitting={refundSubmitting} refundError={refundError} submitRefund={submitRefund}
        />
      )}
    </div>
    </div>
  );
}
